import * as React from 'react'
import { Query } from 'react-apollo'

import Loader from 'main/components/common/Loader'
import { AppContext } from 'main/framework'
import { Italic, spacing } from 'main/ui'

import {
  CARD,
  PAYMENT_METHODS_QUERY,
  PaymentMethods as IData,
  PaymentMethods_paymentMethod as IPaymentMethod,
  PaymentMethods_paymentMethodPending as IPaymentMethodPending,
  PaymentMethodsVariables as IVariables,
} from 'main/gql/paymentMethod'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../common'

import ChoiceSections from './ChoiceSections'
import Notice from './Notice'
import PaymentMethod from './PaymentMethod'
import { Container } from './PaymentMethodSettings.styles'
import ToggleableLink from './ToggleableLink/ToggleableLink'
import { Mode } from './types'

interface IProps {
  mode: Mode
  onChange?: (activate: boolean) => void
}

const PRE = `MAIN-PAYMENT-METHODS-SETTINGS`

export default function PaymentMethodSettings(props: IProps) {
  const { mode, onChange } = props
  const { user } = React.useContext(AppContext)

  if (!user) return <Loader />

  return (
    <Query<IData, IVariables> query={PAYMENT_METHODS_QUERY} variables={{ userId: user!.id }}>
      {({ data, loading, error }) =>
        data && !loading && !error ? (
          <Content
            mode={mode}
            paymentMethod={data.paymentMethod}
            paymentMethodPending={data.paymentMethodPending}
            onChange={onChange}
          />
        ) : (
          <Loader />
        )
      }
    </Query>
  )
}

interface IContentProps {
  mode: Mode
  paymentMethod: IPaymentMethod | null
  paymentMethodPending: IPaymentMethodPending | null
  onChange?: (activate: boolean) => void
}

function Content(props: IContentProps) {
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const { mode, paymentMethod, paymentMethodPending, onChange } = props

  const [choiceSectionsOpen, setChoiceSectionsOpen] = React.useState<boolean>(
    !paymentMethod && !paymentMethodPending,
  )

  React.useEffect(() => {
    if (onChange) {
      paymentMethod || paymentMethodPending ? onChange(false) : onChange(true)
    }
  })

  return (
    <Container>
      {paymentMethod && (
        <PaymentMethod
          name={paymentMethod.source.type === CARD ? t(`${PRE}-CREDIT`) : t(`${PRE}-BANK`)}
          last4={paymentMethod.source.last4}
          type={paymentMethod.source.type}
          brand={paymentMethod.source.brand}
          expMonth={paymentMethod.source.expMonth}
          expYear={paymentMethod.source.expYear}
        />
      )}

      {paymentMethod && paymentMethodPending && <Notice />}

      {paymentMethodPending && (
        <PaymentMethod
          name={paymentMethodPending.accountName}
          last4={paymentMethodPending.accountLast4}
          paymentMethodPending={paymentMethodPending}
        />
      )}

      {(paymentMethod || paymentMethodPending) && (
        <ToggleableLink open={choiceSectionsOpen} onChange={setChoiceSectionsOpen}>
          {t(`${PRE}-ADD`)}
        </ToggleableLink>
      )}

      {(paymentMethod || paymentMethodPending) && choiceSectionsOpen && (
        <div css={{ marginTop: spacing.majorScale(1) }}>
          <Italic>{t(`${PRE}-REPLACE`)}</Italic>
        </div>
      )}

      {choiceSectionsOpen && (
        <ChoiceSections mode={mode} close={() => setChoiceSectionsOpen(false)} />
      )}
    </Container>
  )
}
